/** The activity record's cards (plan §3 "Cards", §2 "Cards"): a `ViewLayout` over the
 *  activity registry (`activitySections`) — About, Place, Visit, Contact & links, Nearby,
 *  Access, Identity & source, Technical (collapsed) — under the record's first panel (Where
 *  it's used, `lead`), full width; and among them, after Nearby, the immediate Documents &
 *  links panel (`documents` — no registry card: nothing in it is a draft field).
 *
 *  The records' width rule: the layout follows the width the cards have in EDIT mode, in both
 *  modes. From 1100 px of content the dock (`PREVIEW_DOCK_WIDTH`, 380 px) sits beside the cards
 *  and takes its column out of that width; two card columns only from 1200 px of card width,
 *  else one in page order. Each card's field grid is pinned to its edit-mode column count
 *  (232 px minimum, 16 px gutters), so entering edit mode re-lays nothing out. The rows
 *  (`activityCardRows`) are the loading skeleton's too, so it stands where the cards land. */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Skeleton } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';

import type { ActivityDetail } from '@/api/activities';
import {
  ACTIVITY_RECORD_SECTIONS,
  type ActivityRecordFieldKey,
  type ActivityRecordSection,
} from '@/api/activityRecord';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { LEDGER_GAP_PX, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import { cardSkeletonShape, RecordCardSkeleton } from '@/features/records/page/RecordStates';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import {
  CARD_GAP_PX,
  RecordCardRows,
  type RecordCardColumns,
  type RecordCardRow,
} from '@/features/records/panels/RecordCardRows';
import { RecordPanel } from '@/features/records/panels/RecordPanel';
import { PREVIEW_DOCK_WIDTH } from '@/features/records/preview/PreviewDock';
import type { ViewFieldRef, ViewSection } from '@/features/records/views/types';

import { DOCUMENTS_ANCHOR_ID, DocumentsPanelSkeleton } from '../parts/DocumentsPanel';

import { activityFieldActions, activityFieldDisplay, type ActivityDisplayContext } from './displays';
import { activitySectionAnchorId, activitySections } from './layout';

/** Two card columns from this much card width (the tour record's rule). */
export const ACTIVITY_TWO_COLUMNS_FROM_PX = 1200;

/** @param width the page content's width (dock included); 0 = unmeasured. */
export function activityColumns(width: number): RecordCardColumns {
  if (width === 0) return { docked: true, editWidth: 0, columns: 1 };
  const docked = width >= SHEET_BELOW_PX;
  const editWidth = docked ? width - PREVIEW_DOCK_WIDTH - LEDGER_GAP_PX : width;
  return { docked, editWidth, columns: editWidth >= ACTIVITY_TWO_COLUMNS_FROM_PX ? 2 : 1 };
}

/** Values that take the whole row: long text, the week, the lists. */
const WIDE: ReadonlySet<ActivityRecordFieldKey> = new Set([
  'description',
  'opening_hours',
  'children',
  'atag',
  'internal_notes',
  'notes',
  'ontrip_description',
  'description_abbrev',
]);

const isWide = (key: string) => WIDE.has(key as ActivityRecordFieldKey);

/** The cards' sections before the detail arrives — every activity's, the legacy texts (laid
 *  out only when they hold something) apart. */
const SKELETON_SECTIONS = new Map(
  activitySections({ id: 0, notes: null, ontrip_description: null, description_abbrev: null }).map((s) => [s.id, s]),
);

/** What the cards' rows hold: the registry's cards, and the Documents & links panel. */
export type ActivityCardId = ActivityRecordSection | 'documents';

/** A card while the activity loads, slot for slot (`cardSkeletonShape`); Technical opens
 *  collapsed — its title and the Show button's room. */
export function activityCardSkeleton(id: ActivityCardId): ReactNode {
  if (id === 'documents') return <DocumentsPanelSkeleton />;
  const section = SKELETON_SECTIONS.get(id);
  if (!section) return null;
  if (id === 'technical') return <RecordCardSkeleton action={<Skeleton variant="rounded" width={72} height={31} />} />;
  return <RecordCardSkeleton blocks={cardSkeletonShape(section, isWide)} />;
}

/** Two columns: About, Visit, Nearby, Identity & source on the left; Place, Contact & links,
 *  Documents & links, Access on the right; Technical across both. */
const LEFT: ActivityCardId[] = ['about', 'visit', 'nearby', 'identity'];
const RIGHT: ActivityCardId[] = ['place', 'contact', 'documents', 'access'];

/** One column, in page order (plan §3): Documents & links after Nearby. */
const PAGE_ORDER: ActivityCardId[] = ACTIVITY_RECORD_SECTIONS.flatMap((s): ActivityCardId[] =>
  s === 'nearby' ? [s, 'documents'] : [s],
);

/** The cards' rows for a column count (under the lead): one column in page order; two as
 *  above. */
export function activityCardRows(columns: 1 | 2): RecordCardRow<ActivityCardId>[] {
  return columns === 2 ? [{ left: LEFT, right: RIGHT }, { full: ['technical'] }] : [{ full: PAGE_ORDER }];
}

/** The cards in the order they stand for a column count: one column in page order; two
 *  columns the left column top to bottom, then the right, then Technical — the order the
 *  stacks read in (and the DOM's, so Tab agrees). The number keys follow it. */
export function activityCardOrder(columns: 1 | 2): ActivityCardId[] {
  return activityCardRows(columns).flatMap((row) => ('full' in row ? [...row.full] : [...row.left, ...row.right]));
}

/** The DOM id of a card or of the Documents & links panel. */
export function activityCardAnchorId(id: ActivityCardId): string {
  return id === 'documents' ? DOCUMENTS_ANCHOR_ID : activitySectionAnchorId(id);
}

function ActivityFieldGrid({ fields, display }: { fields: ViewFieldRef[]; display: ActivityDisplayContext }) {
  const { fieldByKey } = useRecordEditor();
  return (
    <FieldCellGrid
      sx={FIELD_GRID_SX}
      cells={fields.map((f) => {
        const key = f.key as ActivityRecordFieldKey;
        const shown = activityFieldDisplay(key, display);
        const actions = activityFieldActions(key, display);
        return {
          key: `${f.entity}:${f.id}:${f.key}`,
          rec: `${f.entity}:${f.id}`,
          path: f.path,
          wide: isWide(key) && fieldByKey.has(key),
          node: (
            <FieldSlot
              fieldKey={f.key}
              entity={f.entity}
              id={f.id}
              path={f.path}
              inlineChoices={false}
              {...(shown !== undefined ? { display: shown } : {})}
              {...(actions ? { actions } : {})}
            />
          ),
        };
      })}
    />
  );
}

function ActivitySectionPanel({
  section,
  display,
  meta,
}: {
  section: ViewSection<ActivityRecordSection>;
  display: ActivityDisplayContext;
  meta?: ReactNode;
}) {
  return (
    <RecordPanel
      section={section}
      anchorId={activitySectionAnchorId(section.id)}
      renderFields={(g) => <ActivityFieldGrid fields={g.fields} display={display} />}
      {...(meta ? { meta } : {})}
    />
  );
}

/** Technical, collapsed until asked: its header keeps the card in reach of the number keys. */
function TechnicalCard({ section, display }: { section: ViewSection<ActivityRecordSection>; display: ActivityDisplayContext }) {
  const [open, setOpen] = useState(false);
  return (
    <ActivitySectionPanel
      section={open ? section : { ...section, blocks: [] }}
      display={display}
      meta={
        <Button
          size="small"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          data-technical-toggle
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      }
    />
  );
}

export interface ActivityCardsViewProps {
  detail: ActivityDetail;
  display: ActivityDisplayContext;
  /** The page content's width (dock included); 0 = unmeasured. */
  width: number;
  /** The record's first panel, full width above the cards (Where it's used). */
  lead?: ReactNode;
  /** The Documents & links panel, in its place among the cards. */
  documents?: ReactNode;
}

export function ActivityCardsView({ detail, display, width, lead, documents }: ActivityCardsViewProps) {
  const { editWidth, columns } = activityColumns(width);
  const sections = useMemo(() => activitySections(detail), [detail]);
  const by = new Map(sections.map((s) => [s.id, s]));

  const card = (id: ActivityCardId): ReactNode => {
    if (id === 'documents') return documents ?? null;
    const section = by.get(id);
    if (!section) return null;
    if (id === 'technical') return <TechnicalCard key={id} section={section} display={display} />;
    return <ActivitySectionPanel key={id} section={section} display={display} />;
  };

  return (
    <Box data-activity-cards data-columns={columns} sx={{ display: 'grid', gap: `${CARD_GAP_PX}px`, minWidth: 0 }}>
      {lead}
      <RecordCardRows rows={activityCardRows(columns)} editWidth={editWidth} card={card} />
    </Box>
  );
}
